import React from 'react';
import { bool, string } from 'prop-types';
import { propTypes } from '../../util/types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import config from '../../config';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
  NamedRedirect,
} from '../../components';
import { TopbarContainer } from '../../containers';

import creatorImage from './images/creator-image.avif';
import organizationImage from './images/organization-image.avif';

import css from './RoleSelectionPage.module.css';

export const RoleSelectionPageComponent = props => {
  const {
    intl,
    scrollingDisabled,
    currentUser,
  } = props;

  const title = intl.formatMessage(
    {
      id: 'RoleSelectionPage.schemaTitle',
    },
    { siteTitle: config.siteTitle }
  );

  // Get user's current role
  const userRole = currentUser?.attributes?.profile?.publicData?.role;
  const isAudienceUser = userRole === config.userRoles['audience'];
  const isCreatorUser = userRole === config.userRoles['creator'];
  const isOrganizationUser = userRole === config.userRoles['organization'];

  // If user is logged in as Creator, redirect to Creator dashboard
  if (currentUser && isCreatorUser) {
    return <NamedRedirect name="CreatorHomePage" />;
  }

  // If user is logged in as Organization, redirect to Organization dashboard
  if (currentUser && isOrganizationUser) {
    const orgProfiles = currentUser?.attributes?.profile?.publicData?.organizationProfiles || [];
    const orgId = orgProfiles.length > 0 ? orgProfiles[0].id : null;
    if (orgId) {
      return <NamedRedirect name="OrganizationHomePage" params={{ orgId }} />;
    }
  }

  // Render card content
  const renderCard = (role, image, titleId, descriptionId, buttonId) => {
    // Audience users go to bridge signup, unauthenticated users go to signup
    const linkName = isAudienceUser ? 'BridgeSignupPage' : 'SignupPage';

    return (
      <NamedLink
        className={css.signupBox}
        name={linkName}
        params={{ role }}
      >
        <img
          className={css.signupBoxImage}
          src={image}
          alt={intl.formatMessage({ id: titleId })}
        />
        <h2 className={css.signupBoxTitle}>
          <FormattedMessage id={titleId} />
        </h2>
        <p className={css.signupBoxDescription}>
          <FormattedMessage id={descriptionId} />
        </p>
        <span className={css.signupBoxButton}>
          <FormattedMessage id={buttonId} />
        </span>
      </NamedLink>
    );
  };

  // Determine page title and subtitle based on user state
  const pageTitleId = isAudienceUser
    ? 'RoleSelectionPage.upgradeTitle'
    : 'RoleSelectionPage.title';
  const pageSubtitleId = isAudienceUser
    ? 'RoleSelectionPage.upgradeSubTitle'
    : 'RoleSelectionPage.subTitle';

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled} referrer="origin">
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.layoutWrapperMain}>
          <div className={css.root}>
            <div className={css.content}>
              <div className={css.titleWrapper}>
                <h1 className={css.title}>
                  <FormattedMessage id={pageTitleId} defaultMessage="Join as" />
                </h1>
                <p className={css.subTitle}>
                  <FormattedMessage id={pageSubtitleId} defaultMessage="Choose your role" />
                </p>
              </div>

              <div className={css.contentWrapper}>
                <div className={css.signupBoxes}>
                  {/* Creator & Crew Card */}
                  {renderCard(
                    config.userRoles['creator'],
                    creatorImage,
                    'RoleSelectionPage.creatorTitle',
                    'RoleSelectionPage.creatorDescription',
                    'RoleSelectionPage.creatorButton'
                  )}

                  {/* Organization Card */}
                  {renderCard(
                    config.userRoles['organization'],
                    organizationImage,
                    'RoleSelectionPage.organizationTitle',
                    'RoleSelectionPage.organizationDescription',
                    'RoleSelectionPage.organizationButton'
                  )}
                </div>

                {/* Footer text - only show for non-logged-in users */}
                {!isAudienceUser && (
                  <div className={css.footerText}>
                    <p className={css.helpText}>
                      <FormattedMessage id="RoleSelectionPage.helpText" />
                    </p>
                    <p className={css.teamInviteText}>
                      <FormattedMessage
                        id="RoleSelectionPage.teamInviteText"
                        values={{
                          teamInviteLink: (
                            <NamedLink className={css.teamInviteLink} name="LoginPage">
                              <FormattedMessage id="RoleSelectionPage.teamInviteLink" />
                            </NamedLink>
                          ),
                        }}
                      />
                    </p>
                  </div>
                )}

                {/* Show different footer for Audience users */}
                {isAudienceUser && (
                  <div className={css.footerText}>
                    <p className={css.helpText}>
                      <FormattedMessage
                        id="RoleSelectionPage.audienceHelpText"
                        defaultMessage="Upgrading your account is free and gives you access to more features."
                      />
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

RoleSelectionPageComponent.defaultProps = {
  currentUser: null,
};

RoleSelectionPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
  };
};

const RoleSelectionPage = compose(
  connect(mapStateToProps),
  injectIntl
)(RoleSelectionPageComponent);

export default RoleSelectionPage;
